/**
 * Ryan O'Dowd
 * 2024-02-20
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  GOALS_ROUTE,
  NEW_GOAL_ROUTE,
} from '@utils/store/actions';
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import Button from '@components/common/Button';
import EmptyScreen from '@components/common/EmptyScreen';
import Icon from '@react-native-vector-icons/material-design-icons';
import Menu from '@components/common/Menu';
import Text from '@components/common/Text';
import WidgetBase from '@components/WidgetBase';
import getStyles from './styles';
import hooks from '@hooks';

const RecentGoals = (props: RecentGoalsProps) => {
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const [_isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  return (
    <WidgetBase.Container
      layout={props.layout}
      cellSize={props.cellSize}
      walkthroughProps={props.walkthroughProps}
    >
      <WidgetBase.Header
        title={i18n('recentGoals')}
        onPress={() => navigation.navigate(GOALS_ROUTE)}
        rightComponent={
          <Menu
            statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
            anchor={
              <Icon
                style={styles.actionIcon}
                name='dots-vertical'
                color={styles.colors.brickTitle}
                onPress={() => setIsOverflowMenuOpen(true)}
              />
            }
            visible={_isOverflowMenuOpen}
            onDismiss={() => setIsOverflowMenuOpen(false)}
          >
            <Menu.Item
              title={i18n('createNewGoal')}
              onPress={() => {
                setIsOverflowMenuOpen(false);
                navigation.navigate(NEW_GOAL_ROUTE);
              }}
            />
          </Menu>
        }
      />
      <WidgetBase.Content noBackground={props.recentGoals.length > 0}>
        {props.recentGoals.length === 0
          ? (
            <EmptyScreen.Wrapper>
              <EmptyScreen.Text>{i18n('noActiveGoals')}</EmptyScreen.Text>
              <Button
                mode='contained'
                theme={styles.colors.accent}
                onPress={() => navigation.navigate(NEW_GOAL_ROUTE)}
              >
                {i18n('createNewGoal')}
              </Button>
            </EmptyScreen.Wrapper>
          ) : (
            props.recentGoals.map((goal) => { // @TODO: abstract with SideDrawer
              return (
                <View key={goal.key}>
                  <TouchableOpacity
                    style={[styles.option, styles.recentGoals]}
                    onPress={goal.onPress}
                  >
                    {goal.iconComponent}
                    <View style={styles.optionTextWrapper}>
                      <Text
                        style={[styles.optionText, goal.textStyles]}
                        numberOfLines={2}
                        adjustsFontSizeToFit={true}
                      >
                        {goal.title}
                      </Text>
                      {goal.metadata}
                    </View>
                  </TouchableOpacity>
                </View>
              );
            })
          )
        }
      </WidgetBase.Content>
    </WidgetBase.Container>
  );
};

class RecentGoalsProps {
  layout: {
    x: number,
    y: number,
    w: number,
    h: number,
  }; // @TODO: type
  cellSize: number;
  recentGoals: any[];
  walkthroughProps: {
    isVisible: boolean,
    onClose: () => void,
    title: string,
    body: string,
    placement: 'left' | 'right' | 'top' | 'bottom' | 'center';
  }; // @TODO: type
};

export default RecentGoals;
